import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { auth } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import Dashboard from './pages/Dashboard';
import Claims from './pages/Claims';
import Agreements from './pages/Agreements';
import NewClaim from './pages/NewClaim';
import Login from './pages/Login';
import Apply from './pages/Apply';
import TrainingVideo from './components/TrainingVideo';
import ResetPassword from './pages/ResetPassword';   // Record 688: the reset page on our own domain
import Attest from './pages/Attest';   // ruling 330 Part 3: the no-login officer attestation page
import { trainingFor } from './data/trainingModules';   // section 492: the six role videos, one per part of the platform
import ClaimDetail from './pages/ClaimDetail';
import DisciplineCases from './pages/DisciplineCases';
import WarRoom from './pages/WarRoom';
import LocalOfficePage from './pages/LocalOffice';   // ruling 320: the Local's own officers' work area
import QuickFile from './pages/QuickFile';
import Readiness from './pages/Readiness';
import LCDesk from './pages/LCDesk';
import Applications from './pages/Applications';   // section 348: the application desk (plan Part C, ruling 207)
import GCDesk from './pages/GCDesk';
import BulletinBoards from './pages/BulletinBoards';   // ruling 173: the committee and local boards
import Messages from './pages/Messages';   // section 776: a member and the officers who represent him
import MemberFiles from './pages/MemberFiles';   // 6a-06 (section 250; ruling 132): the member's digital file
import { mayOpen, refusalText } from './data/roleScreens';   // ruling 170: one list of screens per role
import RulingRef from './components/RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer
import UpdateBanner, { navigateFresh, takeResumePage } from './components/UpdateBanner';   // section 517: a newer version is live; section 522: menu navigation loads it
import AddressReminder from './components/AddressReminder';   // Record 610 (ruling 259): the sign-in reminder for a missing mailing address
import UnionCalendar from './pages/UnionCalendar';   // ruling 264
import Reports from './pages/Reports';   // section 533: the officer's reports
import HandbookLink from './components/HandbookLink';
import MemberRights from './pages/MemberRights';   // ruling 306: members' LMRDA rights, one quiet link at the foot of every screen   // ruling 302 (D7): each role's own handbook, from private Storage
import Onboarding from './pages/Onboarding';   // Record 675: one page, every role's part in coming onto the platform
import TwoStepSetup from './components/TwoStepSetup';   // ruling 303 (D4): authenticator-app two-step sign-in for officers
import { scopeCapNotice } from './data/scopeCap';   // T5 item 20: one banner when a territory list passes 30
import { PLATFORM_NAME } from './installation';   // section 768: the installation's own name (ruling 312)

// THE NAV SPLIT (weekend plan, batch 3): each office lands on its own page instead of all eight
// items in one undifferentiated list for everyone.
//
// WHAT THIS DOES NOT DO, AND THE REASON IS BOB'S OWN STANDING TEST. It removes NOTHING from
// anybody. The test he gave on 2026-08-26 for any change is that we "don't do any harm to the end
// user's abilities", and hiding a nav item is exactly that harm: a General Chairperson who also
// files a claim, a Local Chairperson who needs the Agreements library at 2am, a member who wants
// the audit log - every one of them loses a capability the platform already gave them, to buy a
// shorter menu. The weekend plan's words are "each office LANDS ON its own page", and landing is
// what this changes.
//
// RULING 170 (2026-09-17, Bob: "Everyone at every level only needs see those things that their role
// mandates") SUPERSEDES the 26 August test for a MEMBER: the member set in src\data\roleScreens.js
// carries no filing door (ruling 105 already refuses the write). Officers still lose nothing.
//
// So the split is three things and no fourth:
//   1. WHERE YOU LAND. An officer opens on his desk, a member on the Dashboard.
//   2. WHAT IS AT THE TOP. Your own desk first, then the shared library.
//   3. A SECTION LABEL, so the two groups read as two groups.
// Every item every role could reach before, it can still reach.
const LANDING_BY_ROLE = { general_chair: 'committee', local_chair: 'desk', admin: 'committee', gc_onboarding: 'onboarding' };

// The shared library - identical for every signed-in role, deliberately.
//
// FOUR ITEMS WERE REMOVED FROM THE NAV ON 2026-08-31, AND NOT ONE OF THEM WAS A CAPABILITY.
//
// Bob, from the signed-in screen: "there are some blank pages in ycp (documents, audit log)…
// that needs to be reviewed and considered deeply for an improved presentation without creating
// curious onlookers who have no legitimate reason to have access."
//
// He is describing something worse than an over-generous menu, and the router says so plainly:
// `documents` and `audit-log` HAVE NO CASE IN renderPage(). Neither do `users` and `settings`.
// All four fell through to `default:` and rendered ComingSoon - a hard hat and the words "This
// section is under construction" - to every signed-in member.
//
// WHY REMOVING THEM DOES NOT OFFEND THE STANDING TEST OF 2026-08-26. That test forbids doing
// "harm to the end user's abilities", and the note below this one is right that hiding a real
// page is exactly that harm. THESE ARE NOT REAL PAGES. There is no ability behind them to lose.
// What the nav item does today is ADVERTISE a capability that does not exist - which is the same
// defect as the three false statements removed from ClaimDetail.js on this date, in a different
// place. Removing a false promise is not removing an ability.
//
// AND THE AUDIT LOG IS THE WORST OF THE FOUR, for a reason ruling 79 names. Bob's words on the
// audit trail: "As a member, I'd be happy to hear it, never believe it." A member told that
// everything the office does to his claim is recorded, who then opens Audit Log and finds a
// construction sign, has been handed the strongest possible reason not to believe it.
//
// WHAT HAS TO BE TRUE BEFORE EACH COMES BACK:
//   audit-log   Ruling 86 - the member sees the history of his OWN claim, every officer action,
//               who and when. That page is this nav item's real content, and route (a)'s
//               `updatedBy` (2026-08-31) is the field that makes its rows attributable. Build the
//               page, then restore the item.
//   documents   A route, and a decision on whose documents it lists. S-8 is open: claim documents
//               are still readable by any authenticated account and the storage rule that fixes
//               it is written and undeployed. Do not surface a documents index over an exposure.
//   users       Admin only, and it is provisioning - scripts\set-roles.js does this today.
//   settings    Admin only. Nothing is configurable from a screen yet.
//
// NAV-1 in scripts\ycp-verify.js now fails if a nav item has no route, so this cannot come back
// by accident. The check is the control; this comment is only the reason.
// THE SIDEBAR, AS A TREE - ruling 129 (2026-09-14, task list section 226). Bob, from the signed-in
// screen: "should the dashboard be placed where it is? should it be at the top with subcategories
// below it with their own sub categories?" Yes on both. One item at the top, then groups, each group
// a heading with its own pages under it. A group appears only for a role that can open something in
// it; nothing is removed from anyone who could open it (the standing test of 2026-08-26).
//
//   Dashboard
//   Claims          Claims · New Claim
//   Discipline      Cases · New Case            (Bob: "should discipline be addressed in the same
//                                                manner as claims and new claim?" - yes, and it is)
//   Agreements
//   My Desk                                     local_chair, general_chair, admin
//   The Committee   The Committee · Readiness · The War Room     general_chair, admin (ruling 127)
//
// Every leaf keeps the declarative id-plus-label shape NAV-1 in scripts\ycp-verify.js reads (and it
// reads comments too - cycle 226 failed on an example written here), so a door with no room behind it
// still fails the cycle. A group heading carries no id and is not a door.
export function navForRole(user) {
  // Record 675: the onboarding-only General Chairperson account has one screen, and it is this.
  if (user?.role === 'gc_onboarding') return [{ id: 'onboarding', label: 'Onboarding', icon: '🧭' }];
  const officer = user?.role === 'local_chair' || user?.role === 'general_chair' || user?.isAdmin;
  const gc = user?.role === 'general_chair' || user?.isAdmin;
  const nav = [
    { id: 'dashboard', label: 'Dashboard', icon: '⬛' },
    { group: 'Claims', icon: '📋', items: [
      { id: 'claims', label: 'Claims', icon: '📋' },
      { id: 'new-claim', label: 'New Claim', icon: '➕' },
    ] },
    { group: 'Discipline', icon: '⚖️', items: [
      { id: 'discipline', label: 'Cases', icon: '⚖️' },
      { id: 'new-case', label: 'New Case', icon: '➕' },
    ] },
    { id: 'agreements', label: 'Agreements', icon: '📄' },
    // RULING 173: a board is an address, not a page per committee. Everyone sees the item; who may
    // POST to which board is srcdataulletinBoard.js and the /bulletinPosts rules, not this list.
    { id: 'boards', label: 'Bulletin Boards', icon: '📌' },
    { group: 'Communication', icon: '✉️', items: [
      { id: 'messages', label: 'Messages', icon: '✉️' },   // section 776 (Bob 2026-10-22): the platform's own messages
      { id: 'calendar', label: 'Calendar', icon: '📅' },   // ruling 264: the union calendar
    ] },
  ];
  // Section 794 (Bob 2026-10-24/25): the officer screens were pushed in flat, turning the sidebar into
  // an ungrouped dump. They now live under one "My Work" group. Every id/route is unchanged (NAV-1);
  // cut() filters each item by mayOpen and drops the group if a role sees none (e.g. local-office).
  if (officer) nav.push({ group: 'My Work', icon: '🗂️', items: [
    { id: 'desk', label: 'My Desk', icon: '🗂️' },
    { id: 'applications', label: 'Applications', icon: '📝' },   // section 348; LC sees what needs him (194.3), GC all in committee (207.1)
    { id: 'member-files', label: 'Member Files', icon: '📁' },   // 6a-06: the caseload by member, not by claim
    { id: 'reports', label: 'Reports', icon: '📊' },
    { id: 'local-office', label: 'My Local Office', icon: '🧾' },   // ruling 320 / section 533; gated by mayOpen via cut()
  ] });
  if (gc) {
    // ISS-26 and batch 3. Committee-wide provisioning and the unrouted queue are a General
    // Chairperson's work; a Local Chairperson has no committee-wide act to take on either.
    nav.push({ group: 'The Committee', icon: '🏛️', items: [
      { id: 'committee', label: 'The Committee', icon: '🏛️' },
      { id: 'readiness', label: 'Readiness', icon: '🧰' },
      { id: 'committee-board', label: 'Committee Board', icon: '📊' }, { id: 'war-room', label: 'The War Room', icon: '⚔️' },   // ruling 127: the GC's private workspace, shown only to the office that owns it; Record 656 (Bob 2026-10-12, "All"): the committee's claims board is its own screen
    ] });
  }
  // RULING 170 (2026-09-17): the tree is cut to the role's screens by the one list in
  // src\data\roleScreens.js - the same list renderPage() refuses on. A member (ruling 105) loses
  // the filing doors here; an officer keeps everything he had.
  const cut = (n) => (n.items ? { ...n, items: n.items.filter(i => mayOpen(user, i.id)) } : n);
  return nav.map(cut).filter(n => (n.items ? n.items.length > 0 : mayOpen(user, n.id)));
}

// Kept for anything that still asks the old question; answers from the tree.
export function deskItemsForRole(user) {
  return navForRole(user).flatMap(n => n.items ? n.items : [n]).filter(n => ['dashboard', 'desk', 'committee', 'readiness', 'committee-board', 'war-room'].includes(n.id));
}

export default function App() {
  const [user, setUser] = useState(undefined);
  const [page, setPage] = useState('dashboard');
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [selectedCaseId, setSelectedCaseId] = useState(null);   // 6a-06
  const [memberKey, setMemberKey] = useState(null);   // section 493: the member whose file a tally name opens
  const [claimsFilter, setClaimsFilter] = useState(null);
  const [draftClaimId, setDraftClaimId] = useState(null);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [applying, setApplying] = useState(false);   // ONB-1: the public application form
  const [helpVideo, setHelpVideo] = useState(false);   // Bob to Greg 2026-10-02: the onboarding video only on request before sign-in

  // Navigation and selection belong to ONE signed-in identity. App is never unmounted across
  // a sign-out/sign-in, so without this every one of them survives a change of user -- and
  // two officers of the same committee can read the same claims, which means the next person
  // to sign in lands silently on the previous person's open record.
  const signedInUidRef = useRef(null);

  const TIMEOUT_MS = 20 * 60 * 1000;   // 20 minutes
  const WARNING_MS = 15 * 60 * 1000;   // warn at 15 minutes
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  const resetSessionTimer = useCallback(() => {
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    setSessionWarning(false);
    if (user) {
      warningRef.current = setTimeout(() => setSessionWarning(true), WARNING_MS);
      timeoutRef.current = setTimeout(() => {
        setSessionWarning(false);
        signOut(auth);
      }, TIMEOUT_MS);
    }
  }, [user]);

  useEffect(() => {
    const events = ['mousedown','mousemove','keypress','touchstart','scroll','click'];
    events.forEach(e => window.addEventListener(e, resetSessionTimer));
    resetSessionTimer();
    return () => events.forEach(e => window.removeEventListener(e, resetSessionTimer));
  }, [user, resetSessionTimer]);

  useEffect(() => {
    const authResolved = { current: false };
    const timer = setTimeout(() => {
      if (!authResolved.current) setUser(null);
    }, 5000);
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      authResolved.current = true;
      clearTimeout(timer);

      // Identity changed (including to or from signed-out): drop the previous user's
      // navigation, selected claim, filter and draft. Guarded on the uid so a token
      // refresh cannot reset navigation out from under someone mid-session.
      const uid = firebaseUser ? firebaseUser.uid : null;
      const previousUid = signedInUidRef.current;
      if (uid !== signedInUidRef.current) {
        signedInUidRef.current = uid;
        // Land the officer on his own desk rather than on the shared Dashboard. The role is read
        // from the token below; it is not known yet at this point for a fresh sign-in, so the
        // landing page is set again once the claims resolve. Dashboard remains the fallback and
        // remains reachable by everyone.
        setPage('dashboard');
        setSelectedClaimId(null);
        setClaimsFilter(null);
        setDraftClaimId(null);
      }

      if (firebaseUser) {
        const token = await firebaseUser.getIdTokenResult();
        const c = token.claims || {};
        const roleDisplayMap = {
          'admin': 'Platform Administrator',
          'general_chair': 'General Chairperson',
          'local_chair': 'Local Chairperson',
          'tester': 'Platform Tester',
          'gc_onboarding': 'General Chairperson (onboarding)',   // Record 675
          'member': 'Member',   // ruling 105 (2026-09-14): an issued role; to the rules it is a claimant (owner rights only)
        };
        // Ruling 245 item 3: a token with no role claim is NOT a tester. It is an account nobody has provisioned.
        const role = c.role || null;
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: c.name || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Member',
          role: role,
          isAdmin: role === 'admin',
          committee: c.committee || null,
          onboardingCommittee: c.onboardingCommittee || null,   // Record 675: the onboarding account's committee (not `committee`, by design)
          scopes: Array.isArray(c.scopes) ? c.scopes : [],
          localOffices: Array.isArray(c.localOffices) ? c.localOffices : [],   // ruling 320: offices of a Local (S&T, President ...), by designation
          roleDisplay: role ? (roleDisplayMap[role] || role) : 'Not yet provisioned',
        });
        // WHERE THIS OFFICE LANDS. Only on a change of identity, and only when the role has one -
        // guarded on the uid so a token refresh cannot yank an officer off the page he is reading
        // mid-session, which is the exact defect the signedInUidRef guard above exists to prevent.
        if (uid !== previousUid) {
          const landing = LANDING_BY_ROLE[role === 'admin' ? 'admin' : role];
          const resume = takeResumePage();   // section 522: a menu click that loaded the new version lands where it was headed
          if (resume) setPage(resume);
          else if (landing) setPage(landing);
        }
      } else {
        setUser(null);
      }
    });
    return () => { authResolved.current = true; clearTimeout(timer); unsubscribe(); };
  }, []);

  // Record 688: the password reset link opens our own page (Greg's notes 4-4.3), signed in or not.
  if (window.location.pathname === '/auth/action') return <ResetPassword />;
  // RULING 330 Part 3: the officer attestation page is a public door, like the reset page - the officer has no account.
  if (window.location.pathname === '/attest') return <Attest />;

  if (user === undefined) {
    return (
      <div className="login-page">
        <div style={{ color: '#8AA4BF', fontSize: 14 }}>Loading...</div>
      </div>
    );
  }

  // ONB-1: THE ONE PUBLIC DOOR. The application form is reached from the sign-in screen and lives
  // outside renderPage() entirely - renderPage() is behind mayOpen() and a role, and an applicant
  // has neither. It is not in navForRole for the same reason: there is no nav until a man is in.
  if (!user) {
    // Section 481 (Bob, 2026-09-27) started the onboarding video 3 s after a person first reached the platform. Bob to
    // Greg Keeley, 2026-10-02 (Greg's login notes 2, 2.2): the video plays "only after a successful first login and moved
    // into Help" - before sign-in it is offered behind a "Need help?" button and plays only when pressed. Signed in, each
    // role's own video still starts on its own (trainingFor, below).
    return (
      <>
        {applying
          ? <Apply onBack={() => setApplying(false)} />
          : <Login onApply={() => setApplying(true)} />}
        {helpVideo
          ? <TrainingVideo module="onboarding" title="Onboarding video" delayMs={0}
              src="/training/onboarding_v7.mp4" captions="/training/onboarding_v7.vtt" />
          : <button type="button" onClick={() => setHelpVideo(true)}
              style={{ position: 'fixed', right: 18, bottom: 18, zIndex: 900, background: '#13263A', color: '#E8A020',
                border: '1px solid #2B5C8A', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>
              Need help? Watch the onboarding video
            </button>}
      </>
    );
  }

  // Ruling 245 item 3: signed in, but no role on the account - no page of the platform, and a plain word why.
  if (!user.role) {
    return (
      <div className="login-page">
        <div style={{ maxWidth: 480, padding: 24, border: '1px solid #E8A020', background: '#132235', color: '#DCE7F2', lineHeight: 1.6 }}>
          <div style={{ fontWeight: 700, color: '#E8A020', marginBottom: 8 }}>This account is not yet set up</div>
          <div style={{ fontSize: 13 }}>You are signed in as {user.email}, but the account has no role on the platform yet, so no page is open to it.</div>
          <div style={{ fontSize: 13, marginTop: 8 }}>An account is set up when your application is approved by your Local or General Chairperson. If you were told you are approved, ask him to check your account.</div>
          <div style={{ marginTop: 14 }}>
            <span style={{ fontSize: 12, color: '#C0392B', cursor: 'pointer' }} onClick={() => signOut(auth)}>Sign Out</span>
          </div>
        </div>
      </div>
    );
  }

  // One tree, per role - see navForRole above and Bob's standing test of 2026-08-26.
  const navTree = navForRole(user);

  const renderPage = () => {
    // RULING 170: a page the role's list does not hold is refused here, whatever called setPage().
    if (!mayOpen(user, page)) return <RoleRefused user={user} />;
    switch (page) {
      case 'dashboard': return <Dashboard user={user} onNavigate={(page, param) => { if (param?.filterStatus) { setClaimsFilter(param.filterStatus); } else if (typeof param === 'string') { setSelectedClaimId(param); } setPage(page); }} onViewClaim={(id) => { setSelectedClaimId(id); setPage('claim-detail'); }} initialFilter={claimsFilter} />;
      case 'claims': return <Claims user={user} onNavigate={(p) => { setClaimsFilter(null); setPage(p); }} onViewClaim={(id) => { setSelectedClaimId(id); setPage('claim-detail'); }} initialFilter={claimsFilter} />;
      case 'new-claim': return <QuickFile user={user} onNavigate={setPage} />;
      case 'full-form': return <NewClaim user={user} onNavigate={setPage} draftClaimId={draftClaimId} onDraftLoaded={() => setDraftClaimId(null)} />;
      case 'agreements': return <Agreements user={user} onNavigate={setPage} />;
      case 'member-rights': return <MemberRights user={user} onNavigate={setPage} />;   // ruling 306
      case 'onboarding': return <Onboarding user={user} />;   // Record 675
      case 'claim-detail': return <ClaimDetail claimId={selectedClaimId} user={user} onNavigate={setPage} onResumeDraft={(id) => { setDraftClaimId(id); setPage('full-form'); }} />;
      case 'quick-file': return <QuickFile user={user} onNavigate={setPage} />;
      case 'discipline': return <DisciplineCases key="cases" user={user} />;
      case 'new-case': return <DisciplineCases key="new-case" user={user} initialView="new" />;   // ruling 129 - Discipline gets the door Claims has
      case 'calendar': return <UnionCalendar user={user} />;   // ruling 264
      case 'reports': return <Reports user={user} />;   // section 533
      case 'local-office': return <LocalOfficePage user={user} />;   // ruling 320
      case 'war-room': return <WarRoom user={user} view="meetings" onViewClaim={(id) => { setSelectedClaimId(id); setPage('claim-detail'); }} />;   case 'committee-board': return <WarRoom user={user} view="board" onViewClaim={(id) => { setSelectedClaimId(id); setPage('claim-detail'); }} />;   // section 544: a card opens its claim; Record 656: the board and the meeting room, one each
      case 'boards': return <BulletinBoards user={user} />;
      case 'messages': return <Messages user={user} />;   // section 776
      case 'readiness': return <Readiness user={user} />;
      case 'applications': return <Applications user={user} />;
      case 'desk': return <LCDesk user={user} onNavigate={setPage} onOpenMember={(k) => { setMemberKey(k); setPage('member-files'); }} onViewClaim={(id) => { setSelectedClaimId(id); setPage('claim-detail'); }} />;
      case 'member-files': return <MemberFiles key={memberKey || 'member-files'} initialOpen={memberKey} user={user} onViewClaim={(id) => { setSelectedClaimId(id); setPage('claim-detail'); }} onViewCase={(id) => { setSelectedCaseId(id); setPage('case-detail'); }} />;
      case 'case-detail': return <DisciplineCases key={'case-' + selectedCaseId} user={user} initialCaseId={selectedCaseId} />;   // 6a-06: a case opened from the member's file
      case 'committee': return <GCDesk user={user} onNavigate={setPage} onViewCase={(id) => { setSelectedCaseId(id); setPage('case-detail'); }} onOpenMember={(k) => { setMemberKey(k); setPage('member-files'); }} onViewClaim={(id) => { setSelectedClaimId(id); setPage('claim-detail'); }} />;
      default: return <ComingSoon page={page} />;
    }
  };

  return (
    <div className="app-layout">
      {/* WCAG 2.4.1 (pass 2, 2026-10-25): the first thing in the tab order, invisible until focused.
          Without it a keyboard or screen-reader user walked every sidebar item on every page before
          reaching the screen he opened. The target is the <main> below, which takes tabIndex={-1} so
          it can receive focus without entering the tab order itself. */}
      <a className="skip-link" href="#main-content">Skip to the main content</a>
      <UpdateBanner />
      <nav className="sidebar" aria-label="Platform sections">
        <div className="sidebar-logo" tabIndex={-1}>
          {/* PASS 2 (2026-10-25): the platform's name is branding, not the heading of the screen
              the user is on. It was the only <h1> in the application, so every page announced itself
              as "Labor On The Record". Each screen now carries its own <h1> (below, and in the pages). */}
          <div className="sidebar-logo-name">{PLATFORM_NAME}</div>
          <p>Claims and discipline, on the record</p>
        </div>
        <div className="sidebar-nav">
          {navTree.map(node => node.items ? (
            <div key={node.group} className="nav-group">
              <div className="nav-group-label"><span className="nav-icon">{node.icon}</span>{node.group}</div>
              {node.items.map(item => (
                <div
                  key={item.id}
                  className={`nav-item nav-sub ${page === item.id ? 'active' : ''}`}
                  onClick={() => navigateFresh(item.id, setPage)}
                  tabIndex={-1}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>
          ) : (
            <div
              key={node.id}
              className={`nav-item ${page === node.id ? 'active' : ''}`}
              onClick={() => navigateFresh(node.id, setPage)}
              tabIndex={-1}
            >
              <span className="nav-icon">{node.icon}</span>
              {node.label}
            </div>
          ))}
          {/* THE ADMINISTRATION SECTION WAS REMOVED HERE, 2026-08-31, FOR THE SAME REASON AS THE
              TWO LIBRARY ITEMS ABOVE: neither `users` nor `settings` has a case in renderPage(),
              so both rendered ComingSoon. An admin section that leads nowhere is a worse signal
              than no admin section, because it implies provisioning happens on a screen when it
              happens in scripts\set-roles.js. Restore it when the pages exist - NAV-1 will fail
              if it is restored before they do. */}
        </div>
        <div className="sidebar-footer">
          <div className="user-name">{user.name}</div>
          <div className="user-role">{user.roleDisplay || user.role}</div>
          <HandbookLink user={user} />
          {/* Ruling 306 item 1: one quiet line, always there, never pushed at anyone. */}
          {user.role !== 'gc_onboarding' && <div style={{ marginTop: 6 }}><span style={{ fontSize: 11, color: '#8AA4BF', cursor: 'pointer' }} onClick={() => setPage('onboarding')}>Onboarding: who does what</span></div>}   {/* Record 675 */}
          <div style={{ marginTop: 6 }}><span style={{ fontSize: 11, color: '#8AA4BF', cursor: 'pointer' }} onClick={() => setPage('member-rights')}>Your rights as a union member (LMRDA)</span></div>
          <TwoStepSetup user={user} />
          <div style={{ marginTop: 10 }}>
            <span
              style={{ fontSize: 11, color: '#C0392B', cursor: 'pointer' }}
              onClick={() => signOut(auth)}
            >
              Sign Out
            </span>
          </div>
        </div>
      </nav>
      <main className="main-content" id="main-content" tabIndex={-1}>
        {sessionWarning ? (
          // Ruling 245 item 2: the warning the 15-minute timer always set and nothing ever showed.
          <div role="alert" style={{ position: 'sticky', top: 0, zIndex: 50, background: '#E8A020', color: '#0D1B2A', padding: '10px 16px', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <span>No activity for 15 minutes. You will be signed out in 5 minutes to protect this account.</span>
            <button type="button" className="btn btn-primary" onClick={resetSessionTimer}>Stay signed in</button>
          </div>
        ) : null}
        <AddressReminder key={user.uid} user={user} />   {/* Record 610 (ruling 259) */}
        {scopeCapNotice(user) ? <div role="alert" style={{ margin: '8px 16px', padding: '8px 12px', border: '1px solid #E06C6C', color: '#F4A0A0', fontSize: 12 }}>{scopeCapNotice(user)}</div> : null}   {/* T5 item 20 */}
        {renderPage()}
      </main>
      {(() => {
        // Section 492 (Bob, 2026-09-28: the six role videos go live). The video for the part of the platform
        // this page belongs to, for this role - trainingFor() in src\data\trainingModules.js. Keyed on the
        // module, so moving to another part starts that part's own 3-second clock; the choice is kept on the
        // account (ruling 245 item 1).
        const tv = trainingFor(user, page);
        return tv ? <TrainingVideo key={tv.module} uid={user.uid} {...tv} /> : null;
      })()}
    </div>
  );
}

// RULING 170: what a role sees in place of a screen that is not its own. Ruling 168: the number
// reaches an officer as a tag and a member not at all.
function RoleRefused({ user }) {
  return (
    <div className="page-content">
      <div className="empty-state" style={{ marginTop: 80 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{'\u{1F512}'}</div>
        <div style={{ fontSize: 14, color: '#8AA4BF', textAlign: 'center', maxWidth: 420 }}>{refusalText(user)}<RulingRef user={user} n={170} why="Each role on the platform sees only the screens its role mandates." /></div>
      </div>
    </div>
  );
}

function ComingSoon({ page }) {
  return (
    <div className="page-content">
      <div className="topbar">
        <div>
          <h1 className="topbar-title">{page.charAt(0).toUpperCase() + page.slice(1)}</h1>
        </div>
      </div>
      <div className="empty-state" style={{ marginTop: 80 }}>
        <div className="empty-state-icon">🚧</div>
        <h3>Coming Soon</h3>
        <p>This section is under construction.</p>
      </div>
    </div>
  );
}

